import React, { useEffect, useMemo, useState } from 'react';
import { Users, ShieldCheck, KeyRound, UserPlus } from 'lucide-react';
import PageHeader from './PageHeader';
import { API } from '../apiClient';
import { DEFAULT_PERMISSIONS, PANEL_PERMISSIONS } from '../permissions';
import { APARTMENTS, apartmentSummary, getApartmentIds, toggleSelection } from '../apartments';

function api(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  }).then(async r => {
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.detail || 'API error');
    return body;
  });
}

const emptyForm = {
  name: '',
  username: '',
  password: '',
  role: 'staff',
  language: 'pl',
  active: true,
  property_ids: [],
  permissions: []
};

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [permissionsMeta, setPermissionsMeta] = useState(DEFAULT_PERMISSIONS);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [passwords, setPasswords] = useState({});

  const apartmentOptions = useMemo(() => APARTMENTS, []);

  const permissionKeys = useMemo(
    () => PANEL_PERMISSIONS.map(({ key }) => key).filter((key) => permissionsMeta?.[key]),
    [permissionsMeta]
  );

  const stats = useMemo(() => {
    const active = users.filter(u => u.active !== false).length;
    const admins = users.filter(u => u.role === 'admin').length;
    const staff = users.filter(u => u.role === 'staff').length;
    return [
      { label: 'Aktywni', value: active, tone: 'success' },
      { label: 'Admini', value: admins, tone: 'primary' },
      { label: 'Personel', value: staff, tone: 'info' }
    ];
  }, [users]);

  async function load() {
    const [usersData, permissionsData] = await Promise.all([
      api('/users'),
      api('/permissions').catch(() => ({ available: DEFAULT_PERMISSIONS }))
    ]);
    setUsers(usersData.map((u) => ({ ...u, property_ids: getApartmentIds(u) })));
    if (permissionsData?.available && Object.keys(permissionsData.available).length) {
      setPermissionsMeta(permissionsData.available);
    }
  }

  useEffect(() => {
    load().catch(err => setError(err.message));
  }, []);

  function updateForm(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function togglePermission(list, perm, checked) {
    const base = Array.isArray(list) ? list : [];
    if (checked) return Array.from(new Set([...base, perm]));
    return base.filter(item => item !== perm);
  }

  function permissionLabel(key) {
    return permissionsMeta?.[key]?.label || key;
  }

  function permissionDescription(key) {
    return permissionsMeta?.[key]?.description || '';
  }

  function apartmentNames(ids) {
    const items = apartmentSummary(ids, apartmentOptions);
    if (!items.length) return 'Brak dostępu do apartamentów';
    return items.map(item => item.label).join(', ');
  }

  async function createUser(ev) {
    ev.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api('/users', { method: 'POST', body: JSON.stringify(form) });
      setForm(emptyForm);
      setMessage('Użytkownik utworzony. Może zalogować się nowym hasłem/PIN-em.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function patchUser(userId, payload) {
    setError('');
    setMessage('');
    const updated = await api(`/users/${userId}`, { method: 'PATCH', body: JSON.stringify(payload) });
    setUsers(prev => prev.map(u => u.id === userId ? { ...updated, property_ids: getApartmentIds(updated) } : u));
    setMessage('Zapisano zmianę użytkownika.');
  }

  async function changePassword(userId) {
    const password = (passwords[userId] || '').trim();
    if (!password) {
      setError('Wpisz nowe hasło/PIN.');
      return;
    }
    setError('');
    setMessage('');
    try {
      await api(`/users/${userId}/password`, { method: 'PATCH', body: JSON.stringify({ password }) });
      setPasswords(prev => ({ ...prev, [userId]: '' }));
      setMessage('Hasło/PIN zmienione. Dotychczasowe sesje tego użytkownika zostały wylogowane.');
    } catch (err) {
      setError(err.message);
    }
  }

  function renderApartmentCheckboxes(selectedIds, onChange, disabled = false) {
    const selected = Array.isArray(selectedIds) ? selectedIds : [];
    return apartmentOptions.map((apartment) => {
      const checked = selected.includes(apartment.id);
      return (
        <label key={apartment.id} className="user-permission-item" title={apartment.description}>
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(toggleSelection(selected, apartment.id, e.target.checked))}
          />
          <span>{apartment.label}</span>
        </label>
      );
    });
  }

  return (
    <div>
      <PageHeader
        title="Użytkownicy"
        description="Panel admina: tworzenie kont, role, język, aktywność, dostęp do apartamentów i reset haseł/PIN-ów."
        helpKey="users"
        icon={<Users />}
        stats={stats}
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success-box">{message}</div>}

      <form className="card user-admin-form" onSubmit={createUser}>
        <h2><UserPlus size={18}/> Nowy użytkownik</h2>
        <div className="user-admin-grid">
          <label>
            Imię / nazwa
            <input value={form.name} onChange={e => updateForm('name', e.target.value)} placeholder="np. Maria" required />
          </label>
          <label>
            Login
            <input value={form.username} onChange={e => updateForm('username', e.target.value)} placeholder="np. maria" autoComplete="off" required />
          </label>
          <label>
            Hasło / PIN startowy
            <input value={form.password} onChange={e => updateForm('password', e.target.value)} placeholder="min. 4 znaki" type="password" autoComplete="new-password" required />
          </label>
          <label>
            Rola
            <select
              value={form.role}
              onChange={e => {
                const nextRole = e.target.value;
                setForm(prev => ({
                  ...prev,
                  role: nextRole,
                  property_ids: nextRole === 'admin' && (!prev.property_ids || prev.property_ids.length === 0)
                    ? apartmentOptions.map(item => item.id)
                    : prev.property_ids,
                }));
              }}
            >
              <option value="staff">Personel</option>
              <option value="admin">Administrator</option>
            </select>
          </label>
          <label>
            Język
            <select value={form.language} onChange={e => updateForm('language', e.target.value)}>
              <option value="pl">Polski</option>
              <option value="uk">Українська</option>
              <option value="en">English</option>
            </select>
          </label>
        </div>

        <div className="user-permissions-block">
          <div className="user-permissions-head">
            <strong>Dostęp do apartamentów</strong>
            <p>Zaznacz jeden lub wiele apartamentów, które ten użytkownik ma widzieć po zalogowaniu.</p>
          </div>
          <div className="user-admin-grid user-permissions-grid">
            {renderApartmentCheckboxes(form.property_ids, (next) => updateForm('property_ids', next))}
          </div>
          <div className="user-apartment-summary">
            Ma dostęp do: <strong>{apartmentNames(form.property_ids)}</strong>
          </div>
        </div>

        <div className="user-permissions-block">
          <div className="user-permissions-head">
            <strong>Dostęp do paneli</strong>
            <p>Zaznacz checkboxy stron, do których ten użytkownik ma mieć dostęp po zalogowaniu.</p>
          </div>
          <div className="user-admin-grid user-permissions-grid">
            {permissionKeys.map(perm => (
              <label key={perm} className="user-permission-item" title={permissionDescription(perm)}>
                <input
                  type="checkbox"
                  checked={(form.permissions || []).includes(perm)}
                  onChange={e => updateForm('permissions', togglePermission(form.permissions, perm, e.target.checked))}
                />
                <span>{permissionLabel(perm)}</span>
              </label>
            ))}
          </div>
        </div>

        <button type="submit" disabled={saving}>{saving ? 'Tworzę…' : 'Utwórz konto'}</button>
      </form>

      <div className="card">
        <h2><ShieldCheck size={18}/> Istniejący użytkownicy</h2>
        <div className="user-admin-list">
          {users.map(u => {
            const apartmentIds = getApartmentIds(u);
            return (
              <div className={`user-admin-row ${u.active === false ? 'inactive' : ''}`} key={u.id}>
                <div className="user-admin-main">
                  <strong>{u.name}</strong>
                  <span>@{u.username} · {u.role === 'admin' ? 'Administrator' : 'Personel'} · {u.language || 'pl'}</span>
                  <div className="user-apartment-summary">
                    Ma dostęp do: <strong>{apartmentNames(apartmentIds)}</strong>
                  </div>
                </div>
                <div className="user-admin-controls">
                  <input
                    value={u.name}
                    aria-label={`Nazwa ${u.username}`}
                    onChange={e => setUsers(prev => prev.map(row => row.id === u.id ? { ...row, name: e.target.value } : row))}
                    onBlur={e => patchUser(u.id, { name: e.target.value }).catch(err => setError(err.message))}
                  />
                  <select value={u.role} onChange={e => patchUser(u.id, { role: e.target.value }).catch(err => setError(err.message))}>
                    <option value="staff">Personel</option>
                    <option value="admin">Administrator</option>
                  </select>
                  <select value={u.language || 'pl'} onChange={e => patchUser(u.id, { language: e.target.value }).catch(err => setError(err.message))}>
                    <option value="pl">PL</option>
                    <option value="uk">UK</option>
                    <option value="en">EN</option>
                  </select>
                  <button type="button" className={u.active === false ? 'secondary' : 'danger'} onClick={() => patchUser(u.id, { active: u.active === false }).catch(err => setError(err.message))}>
                    {u.active === false ? 'Aktywuj' : 'Wyłącz'}
                  </button>
                </div>
                <div className="user-permissions-row user-apartment-row">
                  {renderApartmentCheckboxes(apartmentIds, (next) => {
                    setUsers(prev => prev.map(row => row.id === u.id ? { ...row, property_ids: next } : row));
                    patchUser(u.id, { property_ids: next }).catch(err => {
                      setError(err.message);
                      load().catch(reloadErr => setError(reloadErr.message));
                    });
                  }, false)}
                </div>
                <div className="user-permissions-row">
                  {permissionKeys.map(perm => (
                    <label key={perm} className="user-permission-item" title={permissionDescription(perm)}>
                      <input
                        type="checkbox"
                        checked={u.role === 'admin' ? true : (u.permissions || []).includes(perm)}
                        disabled={u.role === 'admin'}
                        onChange={e => {
                          const next = togglePermission(u.permissions, perm, e.target.checked);
                          setUsers(prev => prev.map(row => row.id === u.id ? { ...row, permissions: next } : row));
                          patchUser(u.id, { permissions: next }).catch(err => {
                            setError(err.message);
                            load().catch(reloadErr => setError(reloadErr.message));
                          });
                        }}
                      />
                      <span>{permissionLabel(perm)}</span>
                    </label>
                  ))}
                </div>
                <div className="user-password-reset">
                  <KeyRound size={16}/>
                  <input
                    type="password"
                    placeholder="Nowe hasło/PIN"
                    value={passwords[u.id] || ''}
                    onChange={e => setPasswords(prev => ({ ...prev, [u.id]: e.target.value }))}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => changePassword(u.id)}>Zmień hasło</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
